/**
 * BulkChangeStateModal — set the status (exit status) of every selected item:
 * Active, Deferred or Archived. Deferring can carry a reason and a review
 * date. Items already in the chosen status are left as they are. This is the
 * same move as Classification → Exit status on one item, applied to a group;
 * nothing here invents a new state (canon 04: archived = removed from active
 * consideration, retained, restorable).
 */
import { useMemo, useState } from 'react';
import { Modal, ChoiceGroup, Radio, TextArea, TextInput, Field, Callout } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import type { Item } from '../../../types/prioritization/lifecycle';
import { itemsWord, runBulk, type BulkActionResult } from './index';

type ExitStatus = NonNullable<Item['exit_status']>;

export interface BulkChangeStateModalProps {
  open: boolean;
  selectedIds: string[];
  onClose: () => void;
  onComplete: (result: BulkActionResult) => void;
  /** Pre-chosen status, for a bar button that means one thing ("Defer"). */
  initialState?: ExitStatus;
}

const STATES: Array<{ value: ExitStatus; label: string; description: string }> = [
  { value: 'active', label: 'Active', description: 'In active consideration.' },
  { value: 'deferred', label: 'Deferred', description: 'Out of consideration for now; can be brought back.' },
  { value: 'archived', label: 'Archived', description: 'Leaves every list; record and history stay.' },
];

const VERB: Record<ExitStatus, { present: (items: string) => string; button: (items: string) => string; past: string }> = {
  active: { present: (n) => `set ${n} to Active`, button: (n) => `Set ${n} to Active`, past: 'Set to Active' },
  deferred: { present: (n) => `defer ${n}`, button: (n) => `Defer ${n}`, past: 'Deferred' },
  archived: { present: (n) => `archive ${n}`, button: (n) => `Archive ${n}`, past: 'Archived' },
};

const CONSEQUENCE: Record<ExitStatus, string> = {
  active: 'They return to active consideration.',
  deferred: 'They leave active consideration until someone sets them back to Active. There is no undo for the group.',
  archived: 'They leave every list but keep their record and history. Set one back to Active to bring it back. There is no undo for the group.',
};

export function BulkChangeStateModal({ open, selectedIds, onClose, onComplete, initialState }: BulkChangeStateModalProps) {
  const [target, setTarget] = useState<ExitStatus | null>(initialState ?? null);
  const [reason, setReason] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const items = useItemsStore((s) => s.items);
  const updateItem = useItemsStore((s) => s.updateItem);

  const count = selectedIds.length;
  const alreadyThere = useMemo(
    () =>
      target === null
        ? 0
        : selectedIds.filter((id) => ((items.find((i) => i.id === id)?.exit_status ?? 'active') === target)).length,
    [selectedIds, items, target],
  );
  const changing = count - alreadyThere;

  const apply = async () => {
    if (target === null || busy) return;
    setBusy(true);
    try {
      const result = await runBulk(
        VERB[target].past,
        selectedIds,
        (id) => items.find((i) => i.id === id)?.title ?? id,
        async (id) => {
          const item = items.find((i) => i.id === id);
          if (!item) return { ok: false, reason: 'Item not found' };
          if ((item.exit_status ?? 'active') === target) return { ok: true };
          const updates: Partial<Item> = { exit_status: target };
          if (target === 'deferred') {
            if (reason.trim()) updates.deferReason = reason.trim();
            if (reviewDate) updates.deferReviewDate = new Date(`${reviewDate}T00:00:00`);
          }
          return updateItem(id, updates);
        },
        (done, total) => setProgress({ done, total }),
      );
      onComplete(result);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      dismissible={!busy}
      size="md"
      title={`Change the status of ${itemsWord(count)}`}
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={target === 'active' || target === null ? 'cta' : 'danger'}
            size="sm"
            onClick={() => void apply()}
            disabled={busy || target === null || changing === 0}
          >
            {busy && progress
              ? `Saving… ${progress.done} of ${progress.total}`
              : target === null
                ? 'Choose a status'
                : VERB[target].button(itemsWord(changing))}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ChoiceGroup legend="Status">
          {STATES.map((s) => (
            <Radio
              key={s.value}
              name="bulk-status"
              checked={target === s.value}
              onChange={() => setTarget(s.value)}
              disabled={busy}
              label={s.label}
              description={s.description}
            />
          ))}
        </ChoiceGroup>

        {target === 'deferred' && (
          <div className="space-y-3">
            <Field label="Reason" hint="Optional. Written on each item.">
              <TextArea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="Why these are being set aside"
                disabled={busy}
              />
            </Field>
            <Field label="Review on" hint="Optional. When to look at them again.">
              <TextInput
                size="sm"
                type="date"
                value={reviewDate}
                onChange={(e) => setReviewDate(e.target.value)}
                disabled={busy}
              />
            </Field>
          </div>
        )}

        {target !== null && (
          <Callout tone={target === 'active' ? 'slate' : 'mustard'}>
            {changing === 0 ? (
              <>All {itemsWord(count)} are already {STATES.find((s) => s.value === target)?.label.toLowerCase()}. Nothing to change.</>
            ) : (
              <>
                You are about to {VERB[target].present(itemsWord(changing))}
                {alreadyThere > 0 && ` (${alreadyThere} already ${target === 'active' ? 'active' : target} and left alone)`}.{' '}
                {CONSEQUENCE[target]}
              </>
            )}
          </Callout>
        )}
      </div>
    </Modal>
  );
}
